/**
 * Menu Provider
 * Provides menu context with permission filtering and navigation utilities
 */

import React, { createContext, useContext, useMemo, ReactNode } from 'react';
import { useAuthStore } from '@/store/authStore';
import { isPlatformSuperAdmin } from '@/utils/authUser';
import menuService, {
  MenuModule,
  MenuStructure,
  FlatMenuItem,
  MenuPermission,
} from '@/services/menu.service';

// Context Type
interface MenuContextType {
  // Menu structure
  menuStructure: MenuStructure;
  modules: MenuModule[];
  filteredModules: MenuModule[];
  
  // Utilities
  searchMenuItems: (query: string) => FlatMenuItem[];
  getMenuItemByRoute: (route: string) => FlatMenuItem | undefined;
  getBreadcrumb: (route: string) => string[];
  getModuleByRoute: (route: string) => MenuModule | undefined;
  getRelatedScreens: (route: string, limit?: number) => FlatMenuItem[];
  getQuickActions: (moduleId: string) => FlatMenuItem[];
  
  // Permissions
  hasPermission: (requiredPermissions: MenuPermission[]) => boolean;
  canAccessRoute: (route: string) => boolean;
  
  // All flat items (for favorites, recents, etc.)
  allMenuItems: FlatMenuItem[];
  favoriteableRoutes: FlatMenuItem[];
}

// Create Context
const MenuContext = createContext<MenuContextType | undefined>(undefined);

// Provider Props
interface MenuProviderProps {
  children: ReactNode;
}

/**
 * MenuProvider Component
 * Wraps the application and provides menu context
 */
export const MenuProvider: React.FC<MenuProviderProps> = ({ children }) => {
  const { user } = useAuthStore();

  // Get user permissions from auth store
  const userPermissions = useMemo(() => {
    if (!user || !user.permissions) {
      return [];
    }
    // Permissions are stored as array of strings in format "screen.action"
    return user.permissions;
  }, [user]);

  // Get menu structure
  const menuStructure = useMemo(() => {
    return menuService.getMenuStructure();
  }, []);

  // Get all modules
  const modules = useMemo(() => {
    return menuService.getModules();
  }, []);

  // Filter modules by user permissions
  const filteredModules = useMemo(() => {
    if (!user) {
      return [];
    }
    if (isPlatformSuperAdmin(user)) {
      return modules;
    }
    return menuService.filterModulesByPermissions(modules, userPermissions);
  }, [modules, userPermissions, user]);

  // Get all flat menu items
  const allMenuItems = useMemo(() => {
    return menuService.getFlatMenuItems();
  }, []);

  // Get favoriteable routes
  const favoriteableRoutes = useMemo(() => {
    return menuService.getFavoriteableRoutes();
  }, []);

  const superAdmin = isPlatformSuperAdmin(user);

  // Search menu items
  const searchMenuItems = (query: string): FlatMenuItem[] => {
    const results = menuService.searchMenuItems(query);
    if (superAdmin) return results;
    return results.filter(item =>
      menuService.hasPermission(item.permissions, userPermissions)
    );
  };

  // Get menu item by route
  const getMenuItemByRoute = (route: string): FlatMenuItem | undefined => {
    return menuService.getMenuItemByRoute(route);
  };

  // Get breadcrumb for route
  const getBreadcrumb = (route: string): string[] => {
    return menuService.getBreadcrumb(route);
  };

  // Get module by route
  const getModuleByRoute = (route: string): MenuModule | undefined => {
    return menuService.getModuleByRoute(route);
  };

  // Get related screens
  const getRelatedScreens = (route: string, limit?: number): FlatMenuItem[] => {
    const related = menuService.getRelatedScreens(route, limit);
    if (superAdmin) return related;
    return related.filter(item =>
      menuService.hasPermission(item.permissions, userPermissions)
    );
  };

  // Get quick actions for module
  const getQuickActions = (moduleId: string): FlatMenuItem[] => {
    const actions = menuService.getQuickActions(moduleId);
    if (superAdmin) return actions;
    return actions.filter(item =>
      menuService.hasPermission(item.permissions, userPermissions)
    );
  };

  // Check if user has permission
  const hasPermission = (requiredPermissions: MenuPermission[]): boolean => {
    if (superAdmin) return true;
    return menuService.hasPermission(requiredPermissions, userPermissions);
  };

  // Check if user can access a route
  const canAccessRoute = (route: string): boolean => {
    if (superAdmin) return true;
    const menuItem = menuService.getMenuItemByRoute(route);
    if (!menuItem) {
      // Route not in menu structure - allow access (might be dynamic route)
      return true;
    }
    return menuService.hasPermission(menuItem.permissions, userPermissions);
  };

  const contextValue: MenuContextType = {
    menuStructure,
    modules,
    filteredModules,
    searchMenuItems,
    getMenuItemByRoute,
    getBreadcrumb,
    getModuleByRoute,
    getRelatedScreens,
    getQuickActions,
    hasPermission,
    canAccessRoute,
    allMenuItems,
    favoriteableRoutes,
  };

  return (
    <MenuContext.Provider value={contextValue}>
      {children}
    </MenuContext.Provider>
  );
};

/**
 * useMenu Hook
 * Access menu context in components
 */
export const useMenu = (): MenuContextType => {
  const context = useContext(MenuContext);
  if (!context) {
    throw new Error('useMenu must be used within MenuProvider');
  }
  return context;
};

export default MenuProvider;
